import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, User, PartyPopper, LogOut, Check } from "lucide-react";
import Logo from "@/components/Logo";

const BUSINESS_TYPES = [
  "Healthcare / Behavioral health", "Legal services", "Consulting", "Coaching",
  "Insurance", "Social services / Nonprofit", "Property management", "Other",
];

const TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Anchorage", "Pacific/Honolulu", "America/Toronto", "America/Vancouver",
  "Europe/London", "Europe/Berlin", "Australia/Sydney",
];

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);
}

export default function Setup() {
  const { user, profile, refresh, signOut, memberships, loading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [org, setOrg] = useState({ name: "", business_type: "", time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York" });
  const [me, setMe] = useState({ first_name: "", last_name: "" });
  const [createdOrgId, setCreatedOrgId] = useState(null);

  // Only redirect on initial mount if user already has memberships — don't
  // re-fire on every refresh() call during the setup wizard.
  useEffect(() => {
    if (!loading && memberships && memberships.length > 0 && step === 1 && !createdOrgId) {
      navigate("/app", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (profile?.full_name) {
      const parts = profile.full_name.split(" ");
      setMe({ first_name: parts[0] || "", last_name: parts.slice(1).join(" ") || "" });
    }
  }, [profile]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#57534E]">Loading…</div>;
  // Only bail if the user arrived here already provisioned AND hasn't touched the wizard.
  if (memberships && memberships.length > 0 && step === 1 && !createdOrgId) return null;

  const goStep2 = async () => {
    if (!org.name.trim()) return toast.error("Organization name is required");
    if (!org.business_type) return toast.error("Please choose a business type");
    setSaving(true);
    try {
      const slug = slugify(org.name) + "-" + Math.random().toString(36).slice(2, 6);
      const created = await apiFetch("/organizations", {
        method: "POST",
        body: JSON.stringify({ name: org.name.trim(), slug }),
      });
      setCreatedOrgId(created.organization.id);
      // Insert org preferences (non-blocking if it fails)
      const { error: prefErr } = await supabase.from("org_preferences").insert({
        organization_id: created.organization.id,
        business_type: org.business_type,
        time_zone: org.time_zone,
      });
      if (prefErr) console.warn("org_preferences insert failed:", prefErr.message);
      await refresh();
      setStep(2);
    } catch (e) {
      toast.error(e.message || "Failed to create organization");
    } finally { setSaving(false); }
  };

  const goStep3 = async () => {
    if (!me.first_name.trim() || !me.last_name.trim()) return toast.error("Please enter your name");
    setSaving(true);
    try {
      const fullName = `${me.first_name.trim()} ${me.last_name.trim()}`;
      const { error } = await supabase.from("profiles").update({ full_name: fullName }).eq("id", user.id);
      if (error) { toast.error(error.message); return; }
      await refresh();
      setStep(3);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const finish = async () => {
    await refresh();
    navigate("/app", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] p-4 md:p-6 flex flex-col">
      <div className="max-w-2xl mx-auto w-full flex items-center justify-between py-4">
        <Logo variant="full" size={36} />
        <button onClick={() => signOut().then(() => navigate("/login"))} className="text-xs text-[#57534E] hover:text-[#E8712C] flex items-center gap-1" data-testid="setup-signout">
          <LogOut size={13} /> Sign out
        </button>
      </div>

      <div className="max-w-2xl mx-auto w-full flex-1 flex items-center justify-center py-6">
        <div className="w-full bg-white rounded-3xl border border-[#E8E2D9] p-8 md:p-10" data-testid="setup-page">
          {/* Progress */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className={`h-1.5 w-12 rounded-full ${s <= step ? "bg-[#E8712C]" : "bg-[#E8E2D9]"}`} />
            ))}
          </div>

          {step === 1 && (
            <div data-testid="step-1">
              <div className="w-12 h-12 rounded-2xl bg-[#FDE9D8] flex items-center justify-center mb-4"><Building2 size={22} className="text-[#E8712C]" strokeWidth={1.5} /></div>
              <h2 className="font-display font-bold text-2xl tracking-tight mb-1">Tell us about your organization</h2>
              <p className="text-sm text-[#57534E] mb-6">This is your private workspace. You can update these details later.</p>
              <div className="space-y-4">
                <div>
                  <Label>Organization name *</Label>
                  <Input data-testid="setup-org-name" value={org.name} onChange={(e) => setOrg({...org, name: e.target.value})} placeholder="e.g. Acme Health Services" className="mt-1.5" />
                </div>
                <div>
                  <Label>Business type *</Label>
                  <Select value={org.business_type} onValueChange={(v) => setOrg({...org, business_type: v})}>
                    <SelectTrigger className="mt-1.5" data-testid="setup-biz-type"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>{BUSINESS_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Time zone</Label>
                  <Select value={org.time_zone} onValueChange={(v) => setOrg({...org, time_zone: v})}>
                    <SelectTrigger className="mt-1.5" data-testid="setup-tz"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Button data-testid="setup-continue-1" onClick={goStep2} disabled={saving} className="w-full mt-6 rounded-full bg-[#E8712C] hover:bg-[#C55A1F] py-6">{saving ? "Creating workspace…" : "Continue"}</Button>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-2">
              <div className="w-12 h-12 rounded-2xl bg-[#FDE9D8] flex items-center justify-center mb-4"><User size={22} className="text-[#E8712C]" strokeWidth={1.5} /></div>
              <h2 className="font-display font-bold text-2xl tracking-tight mb-1">Create your profile</h2>
              <p className="text-sm text-[#57534E] mb-6">How should your teammates see you?</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>First name *</Label><Input data-testid="setup-fname" value={me.first_name} onChange={(e) => setMe({...me, first_name: e.target.value})} className="mt-1.5" /></div>
                <div><Label>Last name *</Label><Input data-testid="setup-lname" value={me.last_name} onChange={(e) => setMe({...me, last_name: e.target.value})} className="mt-1.5" /></div>
              </div>
              <Button data-testid="setup-continue-2" onClick={goStep3} disabled={saving} className="w-full mt-6 rounded-full bg-[#E8712C] hover:bg-[#C55A1F] py-6">{saving ? "Saving…" : "Continue"}</Button>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-3" className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#E8F0E4] flex items-center justify-center mx-auto mb-4"><PartyPopper size={28} className="text-[#2C3A29]" strokeWidth={1.5} /></div>
              <h2 className="font-display font-bold text-2xl tracking-tight mb-2">Your ClientDesk workspace is ready</h2>
              <p className="text-sm text-[#57534E] mb-6 max-w-md mx-auto">
                Your dashboard, client management, appointments, tasks, documents and notes are all set up and ready to use.
              </p>
              <div className="text-left bg-[#FAF8F5] rounded-xl p-4 mb-6 space-y-2 max-w-md mx-auto">
                {[
                  "Client management with intake workflows",
                  "Calendar with day, week and month views",
                  "Task lists with priorities and due dates",
                  "Secure document storage",
                  "Team invitations and roles",
                ].map((t) => (
                  <div key={t} className="flex items-center gap-2 text-sm text-[#1C1917]"><Check size={14} className="text-[#2C3A29] flex-shrink-0" /> {t}</div>
                ))}
              </div>
              <Button data-testid="setup-go-dashboard" onClick={finish} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] px-8 py-6">Go to Dashboard</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
